import type { TemplateResult } from 'lit'
import type { BrandSlug } from '../design-system/brands'
import type { LogoVariant } from '../design-system/logos'
import type { FrameView } from './geometry'
import { css, html, nothing, unsafeCSS } from 'lit'
import { logoAspectCss, LOGOS, logoUrl } from '../design-system/logos'
import { geometryCss } from './geometry'
import { iconCalendarCheck } from './icons'

// YYYY/MM/DD from an ISO string. UTC parts (not local) so a Z-midnight seed date
// doesn't shift a day in negative-offset timezones — deterministic across dev/CI TZ.
// 無法解析的字串回 '—'：欄位存在性由 api 層驗證，但值是否為合法日期沒驗——
// 壞資料寧可顯示占位符，不要渲染 NaN/NaN/NaN。
export function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime()))
    return '—'
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${d.getUTCFullYear()}/${mm}/${dd}`
}

// 作答頁的統計期間文案。兩個值都在、且都解析得出來才回字串，否則回 null（整行不顯示）——
// 這兩個欄位在 SurveyContent 上是選填的（後端分階段上線、舊資料可能沒有），渲染成
// 「統計時間：—~—」比不顯示更糟。結果頁那側欄位必填，沿用同一個 fmtDate、不走這支。
export function statsRangeLabel(from: string | undefined, to: string | undefined): string | null {
  if (!from || !to)
    return null
  const start = fmtDate(from)
  const end = fmtDate(to)
  if (start === '—' || end === '—')
    return null
  return `統計時間：${start}~${end}`
}

// Badge 文案：到期倒數（Figma Participant Badge「N 天後結束」）。缺值或無效日期退回通用文案
// （api 層把缺失的 expired_at 正規化成空字串；空字串本會經 NaN 路徑 fallback，這裡顯式
// 寫出以表達意圖，也涵蓋 preview property-fed 的任意輸入）。
export function badgeLabel(expiresAt: string): string {
  if (!expiresAt)
    return '投票中'
  const ms = new Date(expiresAt).getTime() - Date.now()
  if (!Number.isFinite(ms))
    return '投票中'
  const days = Math.ceil(ms / 86_400_000)
  if (days <= 0)
    return '已結束'
  return `${days} 天後結束`
}

export interface FrameOpts {
  // 已由 resolveHeaderBrand 解析的 IP 品牌：決定 header logo 與 .frame[data-brand]（logo 寬高比）。
  // null（preview 尚未選 IP 版位，或問卷 does_show_logo=false）＝不顯示 logo，版面走與 logo 載入失敗同一套 CSS 路徑
  // （見下方 frameView 的 data-logo-broken 說明）。
  brand: BrandSlug | null
  title: string
  // 活動說明：null / 空白時不顯示。
  description?: string | null
  expiresAt: string
  // 封面圖：有值時 header 轉為 Figma Hero（圖 + 漸層遮罩 + 白字）；無值走 Compact（白底深字）。
  imageUrl?: string | null
  progress: number | null // 0-100；null = 不顯示進度條（結果頁）
  // 頁碼（多頁時才帶）：渲染進厚 progress bar 內，右對齊反白字（對齊 Figma Page Info）。
  pageInfo?: { current: number, total: number } | null
  // inline 版型（Figma 298:2435/2436）：header 一律純文字（不渲染 Hero，即使有 imageUrl）、
  // badge 固定「投票」+ 倒數在同一列最右、卡片 radius 16、progress 藥丸進 flow。
  inline?: boolean
  // 統計期間（RFC 3339）。API 尚未提供這兩個欄位，缺值時整行不顯示（見 statsRangeLabel）。
  // 只有 overlay 作答頁會顯示；inline 版型不顯示統計期間。
  statsFrom?: string | undefined
  statsTo?: string | undefined
  // Internal-only phase modifier. This is render state, not a public element property/attribute.
  view: FrameView
}

// Styles for the Frame chrome. Composed into the element's `static styles` so the classes
// below resolve inside the Shadow DOM. Aligned to Figma Modal/Results: a cover-image Hero
// (gradient mask + white text) or a Compact white header, the countdown badge, and a
// full-bleed progress bar pinned to the Hero's bottom edge (desktop; mobile 的 4px 進度條
// 在 action bar 底，見 active.ts）。
// 「desktop/mobile」以 frame 容器寬 640px 分界（container query，嵌入寬度不定、不能用 viewport）：
// modal 桌機 800 → desktop、modal 手機 / sidebar 360 → mobile。
// frameStyles 拆成四段：幾何矩陣（geometry.ts）與 logo 寬高比（logos.ts）都是資料產生的字串，
// 夾在 head 與 tail 之間。位置不能動 —— head 的 `.frame { overflow: hidden }` 與 geometry 的
// `.frame--results { overflow-y: auto }` 同為 (0,1,0)，靠 geometry 在後才不會被蓋掉；tail 的
// `:not(.has-desc)` container 規則與 geometry 的 header 高同為 (0,4,0)，靠它在後才覆蓋得到。
// head/tail 維持 css`` 模板，註解才會被 build/strip-css-literal-comments 剝掉。
const frameHeadStyles = css`
  .frame {
    container: frame / inline-size;
    box-sizing: border-box;
    border: 1px solid var(--mp-border-default);
    border-radius: 8px;
    overflow: hidden;
    background: var(--mp-bg-base);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    /* frame-body 的 padding 由此對外供應 —— sticky action bar 要用負 margin 貼齊卡片邊。 */
    --frame-pad-x: 16px;
    --frame-pad-top: 12px;
    --frame-pad-bottom: 16px;
    /* 內容被裁掉時下緣淡出帶的高：inline 作答區（active.ts）與 sidebar 收合卡（sidebar-shell.ts）
       共用。只能寫 px —— peek.ts 以 parseFloat 讀原字串，rem 或 calc() 會讀錯。 */
    --peek-fade: 28px;
  }
  :host([layout='sidebar']) .frame {
    max-width: 360px;
    margin: 0 auto;
  }
  /* Figma Hero 與 Questionnaire 之間無分隔線：header 只靠 padding 分隔。 */
  .frame-header {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 32px 16px 12px;
  }
  /* Hero（有封面圖）：圖鋪滿 header、黑色漸層遮罩壓底部，內容白字靠下對齊（Figma 166:2419）。 */
  .frame-header.has-hero {
    justify-content: flex-end;
    min-height: 180px;
  }
  .hero-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }
  .frame-header.has-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 25%, rgba(0, 0, 0, 0.45) 65%, rgba(0, 0, 0, 0.9) 100%);
    mix-blend-mode: multiply;
  }
  /* 內容（badge/標題/描述）疊在圖與遮罩之上。:not 排除兩個自帶定位的元素——
     此規則 specificity（0,2,0）高於單一 class，寫進去會把 .hero-img 蓋回 in-flow
     撐爆 header、把 .progress-track 從貼底 absolute 拉回內容流（浮在半空）。 */
  .frame-header > :not(.hero-img):not(.progress-track) {
    position: relative;
  }
  /* IP logo（Figma Logo / Logo-inverse）：寬固定、高＝寬 × 該 brand 的寬高比
     （--mp-logo-aspect 由 logos.ts 依 .frame[data-brand] 產生）。預設是窄容器 in-flow：
     寬 105、與下一列距 12＝header gap 8＋margin 4（Figma 314:4171 / 314:3910 / 314:3461
     logo＋badge 容器 gap 12）。 */
  .frame-logo {
    display: block;
    flex: none;
    width: 105px;
    height: calc(105px * var(--mp-logo-aspect));
    margin-bottom: 4px;
    object-fit: contain;
    object-position: left center;
  }
  /* 陰影只給白色 logo（壓在封面圖上的那一個），原色 logo 不加（設計師規則；值為 Figma Logo Shadow）。
     與「用白色版」是同一個條件：header 正在顯示封面。 */
  .frame-header.has-hero:not([data-hero-broken]) > .frame-logo {
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.2));
  }
  /* logo 載不到：整塊收起。header 內距與 header／面板高由同一個 attribute 退回無 logo 的版本
     （geometry.ts 的 --mp-logo-* 歸零、sidebar-shell 的 X 讓位規則）。 */
  .frame-header[data-logo-broken] > .frame-logo {
    display: none;
  }
  /* 封面圖抓不到（error 事件）：JS 在 header 補 data-hero-broken，整組 Hero 裝飾退回
     Compact 樣式（否則白底上是白字 + 漸層遮罩，內容不可讀）。 */
  .frame-header[data-hero-broken] .hero-img,
  .frame-header[data-hero-broken]::before {
    display: none;
  }
  /* 失敗後不只收掉圖片：Hero 的保留高度與底對齊也必須一起解除，否則白底會留下
     空白圖片框，並把標題推到框的底部。data attribute 在 Lit 重渲後仍保留，故此 fallback
     比只改一次 class 更穩定。 */
  .frame-header.has-hero[data-hero-broken] {
    min-height: 0;
    justify-content: flex-start;
  }
  /* 狀態徽章：brand-soft pill + 日曆 icon + 反白字（Figma Participant Badge）。 */
  .badge {
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 4px;
    height: 31px;
    padding: 4px 12px;
    border-radius: 9999px;
    background: var(--mp-brand-weak);
    color: var(--mp-text-inverse);
    font-size: 14px;
    letter-spacing: 0.5px;
    box-sizing: border-box;
  }
  .badge svg {
    width: 20px;
    height: 20px;
    flex: none;
  }
  /* 標題最多 2 行，超過加省略號（所有版型／phase 共用同一條規則，桌機覆寫只改字級/行高，
     不重設 clamp）。line-clamp 不依賴固定高度，用行數而非 px 夾住，字級改變時仍準。
     瀏覽器支援：帶前綴的 -webkit-line-clamp 自 Chrome 6、Safari 5、Firefox 68 起可用
     （MDN browser-compat-data），早於本 repo 的下限 iOS Safari 16 / Chrome 110 / Firefox 115。 */
  .frame-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    color: var(--mp-text-primary);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    flex-shrink: 0;
  }
  .frame-header.has-hero:not([data-hero-broken]) .frame-title {
    color: #fff;
    text-shadow: 0 0 8px rgba(0, 0, 0, 0.24);
  }
  /* 說明：左側 indicator bar + 16px 文字（Figma Hero description）。Hero 上白字白條。 */
  .frame-desc {
    display: flex;
    gap: 8px;
    align-items: stretch;
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    letter-spacing: 0.5px;
    color: var(--mp-text-secondary);
    white-space: pre-wrap;
  }
  .frame-desc::before {
    content: '';
    flex: none;
    width: 3px;
    border-radius: 8px;
    background: var(--mp-text-secondary);
  }
  .frame-header.has-hero:not([data-hero-broken]) .frame-desc {
    color: #fff;
    text-shadow: 0 0 8px rgba(0, 0, 0, 0.24);
  }
  .frame-header.has-hero:not([data-hero-broken]) .frame-desc::before {
    background: #fff;
  }
  /* Mobile（窄容器）+ Hero：描述不放 Hero、改列在內容區頂（Figma 314:3910/3911）。 */
  .frame-desc--body {
    display: none;
  }
  /* 說明只在渲染於 header 內時截斷：body 版（.frame-desc--body，窄容器 popup）不在
     .frame-header 底下，選擇器天生排除，維持完整顯示。行數依版型/斷點覆寫 --mp-desc-lines
     （見 frameTailStyles 的桌機 inline／cover 覆寫），基準值 1 對應窄容器（實測：標題兩行時
     的安全上限，桌機 compact 沿用同一基準不覆寫）。 */
  .frame-header .frame-desc-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: var(--mp-desc-lines, 1);
  }
  /* overlay 作答頁的統計期間（Figma 280:2376）：卡片內容最上緣獨立一行、20px 行高。
     沿用結果頁 .results-meta 的字級與色票 —— 兩處講的是同一件事，長相不該不一樣。
     inline 版型不顯示統計期間，不走這條。 */
  .frame-stats {
    margin: 0 0 8px;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;
    color: var(--mp-text-meta);
  }
  /* 厚 progress bar（16px）：貼齊 header 底邊、全寬方角（Figma Progress Bar 38:28）。
     半透明 scrim 軌道 + backdrop blur 疊在 Hero 圖上；頁碼右對齊反白字。 */
  .progress-track {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 16px;
    overflow: hidden;
    background: var(--mp-scrim-soft);
    backdrop-filter: blur(10px);
  }
  /* 無圖（Compact，含圖掛掉 fallback）：藥丸形、左右內縮 32、貼 header 底緣
     （Figma 314:4091;128:885：rounded-120、w736/800）。有圖維持滿寬方角貼 hero 底。 */
  .frame-header:not(.has-hero) .progress-track,
  .frame-header[data-hero-broken] .progress-track {
    left: 32px;
    right: 32px;
    border-radius: 9999px;
  }
  .progress-fill {
    position: absolute;
    inset: 0;
    width: 100%;
    transform-origin: left;
    background: var(--mp-brand);
    /* scaleX（非 width）動畫：純 compositor，動畫期間不觸發 layout/paint。 */
    transition: transform 0.3s;
  }
  .progress-pageinfo {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 16px;
    display: flex;
    align-items: center;
    /* Figma Type/Number/XS（頁數）與 XS Strong（當前頁）都是 Open Sans；見 theme.ts。 */
    font-family: var(--mp-font-number);
    font-size: 12px;
    letter-spacing: 0.5px;
    color: var(--mp-text-inverse);
    white-space: nowrap;
  }
  .progress-current {
    font-weight: 600;
  }
  /* header 帶進度條時，內容要讓出貼底 16px 的 bar 高度。 */
  .frame-header.has-progress {
    padding-bottom: 28px;
  }
  .frame-body {
    padding: var(--frame-pad-top) var(--frame-pad-x) var(--frame-pad-bottom);
  }
`

const frameTailStyles = css`
  /* Desktop（frame 容器 ≥640px）：Hero 300px、標題 20px、內距 32（Figma 166:2477）。 */
  @container frame (min-width: 640px) {
    /* 掛在子代而不是 .frame 自己：.frame 就是本 container query 的 container，而 container
       query 只從元素的**祖先** container 選容器（元素不是自己的祖先——這條規範是為了避免
       「查詢結果改變容器尺寸」的循環）。寫成選中 .frame 自己的規則，整條永遠不匹配，桌機會靜靜沿用
       上面那組行動版的 16/12/16。子代拿到宣告，孫代（action bar 的負 margin）照繼承取用。 */
    .frame > * {
      --frame-pad-x: 32px;
      --frame-pad-top: 16px;
      --frame-pad-bottom: 32px;
    }
    .frame-header {
      padding: 32px 32px 16px;
    }
    .frame-header.has-progress {
      padding-bottom: 32px;
    }
    .frame-header.has-hero {
      min-height: 300px;
    }
    .frame-title {
      font-size: 20px;
      letter-spacing: 1.2px;
    }
    /* 桌機 popup：logo 絕對定位在左上、寬 140，不佔 flow。compact（含封面失敗）left 32
       （Figma 810:26191 / 810:26479），cover left 40（Figma 166:2419 / 128:1014）。
       container 是 .frame 自己，所以規則掛在子代上（同上方 .frame > * 的理由）。 */
    .frame:not(.frame--inline) > .frame-header > .frame-logo {
      position: absolute;
      top: 28px;
      left: 32px;
      width: 140px;
      height: calc(140px * var(--mp-logo-aspect));
      margin-bottom: 0;
    }
    .frame:not(.frame--inline) > .frame-header.has-hero:not([data-hero-broken]) > .frame-logo {
      left: 40px;
    }
    /* 桌機 cover：內容內距照 Figma Hero（128:1014 / 314:4291 / 303:2947 / 322:5331：pl40 pr60 py32），
       作答／結果／致謝相同，與 logo 載入成功與否無關。modal 作答頁的 166:2419 是唯一的 px33，不採用——
       其餘四個節點一致取多數。 */
    .frame:not(.frame--inline) > .frame-header.has-hero:not([data-hero-broken]) {
      padding: 32px 60px 32px 40px;
      /* Cover header 空間比 Compact 大得多，實測安全上限到 5 行仍完整顯示、進度條不受擠壓。 */
      --mp-desc-lines: 5;
    }
    /* compact（含封面失敗）有 logo 時內容從 100 起排，避開左上 logo 與右上 X（Figma 810:26191 pt100）。
       logo 載不到時不套用，回到上方的 32。 */
    .frame:not(.frame--inline) > .frame-header:not(.has-hero):not([data-logo-broken]),
    .frame:not(.frame--inline) > .frame-header[data-hero-broken]:not([data-logo-broken]) {
      padding-top: 100px;
    }
    /* 無說明的 Compact header（含圖掛掉 fallback）：不保留說明的行高，否則標題下方會空一塊。
       省下的高度歸 frame-body（卡片總高由 phase 尺寸矩陣決定，不變）。Hero 版不套用——
       圖本身要撐住 header；mobile 的說明本就不在 header，維持 Figma 的保留高度。
       與上面的 phase 固定高度同 specificity，靠後到覆蓋。 */
    .frame .frame-header:not(.has-desc):not(.has-hero),
    .frame .frame-header:not(.has-desc)[data-hero-broken] {
      height: auto;
      min-height: 0;
    }
  }
  /* Mobile overlay：Cover / Compact / broken Hero 都只在 header 留 badge + 標題。answering
     的描述移到 body 頂；results 由 resultsView 放在 stats meta 後；thank-you 不重複描述
     （Figma 314:3910/3911、314:4171/4172、315:5514/6236、322:5798/5865）。
     inline 保留自己的 in-flow header。
     mobile 不在 header 顯示厚進度條（進度條是 action bar 底的 4px 細條，見 active.ts）。 */
  @container frame (max-width: 639.98px) {
    .frame:not(.frame--inline) .frame-header .frame-desc {
      display: none;
    }
    .frame:not(.frame--inline) .frame-desc--body {
      display: flex;
    }
    .frame-header .progress-track {
      display: none;
    }
    .frame-header.has-progress {
      padding-bottom: 12px;
    }
    /* 窄容器的 badge 高 28＝上下 4＋icon 20（Figma 313:2408 / 314:3092 / 314:3461 / 298:2611）；
       寬容器維持上方的 31（Figma 128:1014 / 810:26479 h31）。 */
    .badge {
      height: 28px;
    }
  }
  /* ===== Inline 版型覆寫（Figma 298:2435 desktop / 298:2436 mobile）=====
     一律純文字 header（Hero 由 frameView 直接不渲染）；卡片 radius 16、無陰影、
     內距 mobile 12 / desktop 20；標題兩斷點皆 20px；粗 progress 藥丸進 flow
     （mobile 也用 16px 粗條，蓋掉上面的 display:none；4px 細條由 active.ts 收掉）。
     放在檔尾讓同 specificity 時 inline 規則後到先贏。 */
  .frame--inline {
    /* Inline width follows its host; the phase modifier above owns the target height. */
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    box-shadow: none;
    --frame-pad-x: 12px;
    --frame-pad-top: 16px;
    --frame-pad-bottom: 12px;
  }
  /* 上距 16（Figma 298:2611 pt16）。 */
  .frame--inline .frame-header {
    gap: 8px;
    padding: 16px 12px 0;
  }
  /* inline：logo 一律 in-flow（永不壓在封面上，所以也沒有陰影），與下一列距 16
     （Figma 298:2557 / 298:2611）。header gap 手機 8、桌機 4，差額由 margin 補。 */
  .frame--inline .frame-logo {
    margin-bottom: 8px;
  }
  .frame--inline .frame-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  /* badge 靠左、倒數 meta 靠右，同列（Figma 298:2436 mobile、298:2435 desktop）。 */
  .frame--inline .frame-headline {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .frame--inline .frame-meta {
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: 12px;
    letter-spacing: 0.5px;
    color: var(--mp-text-meta);
  }
  .frame--inline .frame-title {
    font-size: 20px;
    line-height: 30px;
    letter-spacing: 1.2px;
  }
  /* 進度條與其上方內容的間距兩個斷點同為 16（Figma 298:2313 desktop：Questionnaire Container
     結束於 162、Progress y=178；298:2335 mobile：結束於 211、Progress y=227）。 */
  .frame--inline .progress-track {
    position: static;
    display: block;
    margin-top: 16px;
    border-radius: 9999px;
    /* inline header 是 flex column，這是唯一 in-flow（非 absolute）的 progress-track——
       標題/說明截斷前，長內容會把它從 16px 壓扁到 0（flex 預設 shrink）。截斷後理論上不再
       溢出，但仍保留這道防線並以量測確認高度不變。 */
    flex-shrink: 0;
  }
  .frame--inline .frame-header.has-progress {
    padding-bottom: 0;
  }
  @container frame (min-width: 640px) {
    /* 同上：container 自身選不到，宣告掛在子代。 */
    .frame--inline > * {
      --frame-pad-x: 20px;
      --frame-pad-bottom: 20px;
    }
    .frame--inline .frame-header {
      gap: 4px;
      padding: 20px 20px 0;
      /* 桌機 inline header 較寬，實測安全上限為 2 行（窄容器維持基準值 1）。 */
      --mp-desc-lines: 2;
    }
    .frame--inline .frame-logo {
      margin-bottom: 12px;
    }
    .frame--inline .frame-meta {
      font-size: 14px;
    }
    /* inline 桌機 badge 高 28（Figma 298:2557），與其餘窄容器 28／popup 桌機 31 不同——
       inline 與 popup 是各自獨立的稿，這裡照 Figma 對齊即可，不強求跟 popup 桌機一致。 */
    .frame--inline .badge {
      height: 28px;
    }
  }
  /* inline mobile 的描述為純文字（Figma 298:2436 無 indicator bar）。 */
  @container frame (max-width: 639.98px) {
    .frame--inline .frame-desc::before {
      display: none;
    }
    /* inline 的說明兩個斷點都在 header，故窄容器也要在無說明時收掉保留高度
       （overlay 版窄容器的說明在 body，其 header 高度與說明無關，不套用）。 */
    .frame--inline .frame-header:not(.has-desc) {
      height: auto;
      min-height: 0;
    }
  }
`

export const frameStyles = unsafeCSS(
  [frameHeadStyles.cssText, geometryCss(), logoAspectCss(), frameTailStyles.cssText].join('\n'),
)

// 活動說明的顯示文字：null / 全空白視為未填（回空字串），前後空白不進版面。
// 型別檢查而非 truthy 檢查：preview 是 property-fed（不經 api 層正規化），JS 整合方餵進
// 非字串時要退回未填，不能讓 render 拋 TypeError 炸掉整個 widget。
export function descText(description?: string | null): string {
  return typeof description === 'string' ? description.trim() : ''
}

// 圖載入失敗時把 header 與 parent frame 標記為 broken：header marker 收掉圖/遮罩並讓文字退回深色，
// frame marker 則讓 phase 尺寸矩陣改走 Compact target（見 frameStyles）。logo 版本（white/color）
// 與封面是否可用同進退，也用同一組 attribute 表達（.frame-logo 的 src 由 handler 直接改寫）。
// 用 DOM attribute 而非 reactive state：frame 是純函式、錯誤屬罕見終態，Lit 重渲不會清掉它——
// Lit 的 attribute binding 只在「本次 render 的值 !== 它上次 commit 的值」時才寫 DOM，比較的是
// 自己上次 commit 的值而非 DOM 現值；template 結構不變時 .frame-header／.frame-logo 節點沿用
// （與上面既有註解同一機制），所以 handler 改過的 src／attribute 在下次 render 時只要 opts 沒變
// 就會留著；hasHero 改變時 Lit 寫入的新值就是新狀態該有的值。brand 在同一個元素上不會改變
// （survey-element.ts 的 reactive input 契約：brand-id 是 connect-time immutable，換品牌要重建
// 元素），所以不處理「封面失敗期間換 brand」這種情境。
function frameParts(e: Event): { header: Element, frame: Element | null } | null {
  const header = (e.target as HTMLElement).closest('.frame-header')
  return header ? { header, frame: header.closest('.frame') } : null
}

function setLogoSrc(header: Element, key: 'srcColor' | 'srcWhite'): void {
  // 不用 `:scope > .frame-logo`：jsdom（本 repo 測試環境）的 `:scope` combinator 在元素位於
  // Shadow Root 內時無法匹配（已用最小重現確認），改用一般 descendant 選擇器。header 底下只有
  // 一個 .frame-logo（不會有巢狀 frame），效果等價。
  const logo = header.querySelector<HTMLImageElement>('.frame-logo')
  const next = logo?.dataset[key]
  if (logo && next && logo.getAttribute('src') !== next)
    logo.setAttribute('src', next)
}

// 既有行為（收掉圖/遮罩、文字退回深色）＋logo 退回原色版：封面掛掉時 header 已經是白底了，
// 白色 logo 會看不見。
function onHeroError(e: Event): void {
  const p = frameParts(e)
  if (!p)
    return
  p.header.setAttribute('data-hero-broken', '')
  p.frame?.setAttribute('data-hero-broken', '')
  setLogoSrc(p.header, 'srcColor')
}

// 封面換成可載入的圖時（例如 admin 編輯期間清空又放回封面）：標記自癒、logo 回白色版。
function onHeroLoad(e: Event): void {
  const p = frameParts(e)
  if (!p)
    return
  p.header.removeAttribute('data-hero-broken')
  p.frame?.removeAttribute('data-hero-broken')
  setLogoSrc(p.header, 'srcWhite')
}

// logo 自己載不到（與封面圖是否可用無關）：整塊收起，header 內距／高與面板高退回無 logo 的版本
// （geometry.ts 的 --mp-logo-* 歸零、sidebar-shell.ts 的 X 讓位規則）。
function onLogoError(e: Event): void {
  const p = frameParts(e)
  if (!p)
    return
  p.header.setAttribute('data-logo-broken', '')
  p.frame?.setAttribute('data-logo-broken', '')
}

function onLogoLoad(e: Event): void {
  const p = frameParts(e)
  if (!p)
    return
  p.header.removeAttribute('data-logo-broken')
  p.frame?.removeAttribute('data-logo-broken')
}

// The Frame chrome wrapping a phase-specific body.
export function frameView(opts: FrameOpts, body: TemplateResult): TemplateResult {
  const inline = Boolean(opts.inline)
  // inline 一律純文字 header：即使資料帶了封面圖也不渲染 Hero（Hero 僅 sidebar/modal 形式使用）。
  const hasHero = !inline && Boolean(opts.imageUrl)
  // logo 版本首次值：之後由 onHeroError／onHeroLoad 維持，Lit 重渲不回寫（見上方 DOM attribute 的說明）。
  const variant: LogoVariant = hasHero ? 'white' : 'color'
  const hasProgress = opts.progress !== null
  const descriptionText = descText(opts.description)
  // 內層 span 只用來被 line-clamp：indicator bar 掛在 .frame-desc::before，clamp 只夾文字節點
  // 本身，不影響 flex 容器與指示條的版面（見上方 .frame-header .frame-desc-text 規則）。
  const desc = descriptionText
    ? html`<p class="frame-desc"><span class="frame-desc-text">${descriptionText}</span></p>`
    : nothing
  // inline 的 badge 固定「投票」、倒數文字在同列最右（Figma 298:2435/2436 Participant Badge + meta）；
  // overlay 版型維持倒數直接進 badge。
  // 統計期間：只有 overlay 顯示，是卡片最上緣獨立一行（280:2376）；inline 不顯示。
  const statsRange = statsRangeLabel(opts.statsFrom, opts.statsTo)
  const headline = inline
    ? html`<div class="frame-headline">
        <span class="badge">${iconCalendarCheck()}投票</span>
        <span class="frame-meta">${badgeLabel(opts.expiresAt)}</span>
      </div>`
    : html`<span class="badge">${iconCalendarCheck()}${badgeLabel(opts.expiresAt)}</span>`
  // 沒有 brand（preview 尚未選 IP 版位，或問卷 does_show_logo=false）就沒有 logo 可顯示：不渲染 <img>，並直接套用
  // logo-broken 那組版面（geometry.ts 的 --mp-logo-* 歸零、frame.ts／sidebar-shell.ts 的
  // padding-top:100px／X 讓位規則跟著 [data-logo-broken] 走）——與 onLogoError 標記的是
  // 同一個 attribute，只是這裡從第一次 render 就成立，不必等一個永遠不會發生的 img error。
  const noLogo = opts.brand === null
  return html`
    <div class="frame frame--${opts.view} ${hasHero ? 'has-hero' : ''} ${inline ? 'frame--inline' : ''}" data-brand=${opts.brand ?? nothing} ?data-logo-broken=${noLogo}>
      <div class="frame-header ${hasHero ? 'has-hero' : ''} ${hasProgress ? 'has-progress' : ''} ${descriptionText ? 'has-desc' : ''}" ?data-logo-broken=${noLogo}>
        ${hasHero ? html`<img class="hero-img" src=${opts.imageUrl ?? ''} alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer" @error=${onHeroError} @load=${onHeroLoad} />` : nothing}
        ${opts.brand === null
          ? nothing
          : html`<img class="frame-logo"
          src=${logoUrl(opts.brand, variant)}
          data-src-color=${logoUrl(opts.brand, 'color')}
          data-src-white=${logoUrl(opts.brand, 'white')}
          alt=${LOGOS[opts.brand].alt}
          decoding="async" referrerpolicy="no-referrer"
          @error=${onLogoError} @load=${onLogoLoad} />`}
        ${headline}
        <h2 class="frame-title">${opts.title}</h2>
        ${desc}
        ${hasProgress
          ? html`<div class="progress-track">
              <div class="progress-fill" style="transform:scaleX(${Math.max(0, Math.min(100, opts.progress ?? 0)) / 100})"></div>
              ${opts.pageInfo
                ? html`<span class="progress-pageinfo"><span class="progress-current">${opts.pageInfo.current}</span>/${opts.pageInfo.total}</span>`
                : nothing}
            </div>`
          : nothing}
      </div>
      <div class="frame-body">
        ${!inline && opts.view === 'answering' && statsRange
          ? html`<p class="frame-stats">${statsRange}</p>`
          : nothing}
        ${!inline && opts.view === 'answering' && descriptionText
          ? html`<p class="frame-desc frame-desc--body"><span class="frame-desc-text">${descriptionText}</span></p>`
          : nothing}
        ${body}
      </div>
    </div>
  `
}
